import React, { useState } from 'react';
import { numberFormat } from '@airasia-common/libraries/i18n/LocaleHelper';
import { string, element, object, bool } from 'prop-types';
import styled from 'styled-components';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import PhoenixTheme from '@airasia/phoenix-components/components/Theme';
import PaymentDetailModal from '@airasia/phoenix-components/components/PaymentDetailModal';
import Image from '../Image';
import Text from '../Text/Text';
import Row from '../Container/Row';
import Column from '../Container/Column';
import { SubBodyText, CaptionText } from '../Text/Heading';
import Styled from '../../utils/Styled';
import { Card } from '../Card';
import { ExpandIcon } from '../../utils/statics';
import { THEME } from '../../utils/constants';

const PriceCTAWrapper = Styled(Row, {
  justifyContent: 'space-between',
});

const LeftInfo = Styled(Column, {
  justifyContent: 'center',
});

const RightInfo = styled.div`
  display: flex;
  align-items: ${({ ctaNote }) => (ctaNote ? 'center' : 'flex-end')};
  justify-content: flex-end;
  margin-left: -25px;
`;

const DiscountContent = styled(Row)`
  height: 18px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const Strikethrough = styled.div`
  display: flex;
  color: #4c4c50;
  margin-left: 8px;
  text-decoration: line-through;
`;

const CurrencyText = styled.span`
  font-weight: normal;
  font-size: 12px;
  line-height: 20px;
`;

const Value = styled.span`
  font-weight: 500;
  font-size: 14px;
`;

const Title = Styled(SubBodyText, {
  fontWeight: 400,
  fontSize: ['14px', '16px'],
  lineHeight: ['16px', '19px'],
});

const SubTitle = Styled(CaptionText, {
  marginTop: '4px',
  lineHeight: '16px',
  fontSize: ['10px', '12px'],
  fontWeight: 400,
});

const Currency = Styled(Text, {
  marginRight: '4px',
  fontSize: ['12px', '14px'],
  lineHeight: '22px',
});

const Price = Styled(Text, {
  fontSize: '24px',
  lineHeight: '28px',
});

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  ${prop => prop.width && 'width:' + prop.width + ';'}
`;

const RightWrapper = styled.div`
  flex-direction: column;
  width: 100%;
  justify-content: flex-end;
  margin-right: ${({ isPriceBreakDownMweb }) =>
    isPriceBreakDownMweb ? '20px' : ''};
`;

const Button = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 5px;
  width: 8px;
  height: 7px;
  margin-bottom: 12px;
`;

const PriceCTA = ({
  currency,
  price,
  subTitle,
  title,
  rightChildren,
  formatOptions = {},
  ctaNote,
  strikeThrough,
  discount,
  priceBreakDownEnabled,
  data,
  isPriceBreakDownMweb,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const totalAmount = data?.total;
  const priceFormated = numberFormat(Number(price), formatOptions);
  const imageSrc = ExpandIcon;
  return (
    <PriceCTAWrapper>
      <Wrapper>
        <LeftInfo>
          <Title className="price-title">{title}</Title>
          <SubTitle className="price-subtitle">{subTitle}</SubTitle>
        </LeftInfo>
      </Wrapper>
      <Wrapper width={'50%'}>
        {priceBreakDownEnabled && strikeThrough && (
          <RightWrapper isPriceBreakDownMweb={isPriceBreakDownMweb}>
            <DiscountContent>
              {discount && (
                <Card.PriceOffTextV2>
                  {translate('fareSummary.discount', Number(discount))}
                </Card.PriceOffTextV2>
              )}
              <Strikethrough>
                <CurrencyText>{strikeThrough.currency} &nbsp;</CurrencyText>
                <Value>{numberFormat(strikeThrough.value)}</Value>
              </Strikethrough>
            </DiscountContent>
          </RightWrapper>
        )}
        <RightWrapper isPriceBreakDownMweb={isPriceBreakDownMweb}>
          <RightInfo ctaNote={ctaNote}>
            <Currency className="price-currency">{currency}</Currency>
            <Price className="price-amount">
              {isPriceBreakDownMweb ? totalAmount : priceFormated}
            </Price>
            {rightChildren}
            {isPriceBreakDownMweb && (
              <>
                <Button onClick={() => setIsOpen(true)}>
                  <Image src={imageSrc} />
                </Button>
                <PhoenixTheme theme={THEME}>
                  <PaymentDetailModal
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    data={data}
                    theme={THEME}
                  />
                </PhoenixTheme>
              </>
            )}
          </RightInfo>
        </RightWrapper>
      </Wrapper>
    </PriceCTAWrapper>
  );
};

PriceCTA.propTypes = {
  currency: string.isRequired,
  price: string.isRequired,
  subTitle: string.isRequired,
  title: string.isRequired,
  rightChildren: element,
  formatOptions: object,
  ctaNote: bool,
  strikeThrough: object,
  discount: string,
  priceBreakDownEnabled: bool,
  data: object,
  isPriceBreakDownMweb: bool,
};

PriceCTA.defaultProps = {
  priceBreakDownEnabled: false,
  isPriceBreakDownMweb: false,
};

export default PriceCTA;
